import { firebase } from "../config/configureFirebase";
import { push, set } from "firebase/database";
import { RequestPushMsg } from "./NotificationFunctions";

const INVALID_TOKENS = new Set(["web", "token_error", "init", ""]);

export function getPushTokenStatus(token) {
  if (!token || typeof token !== "string") {
    return "missing";
  }
  const trimmed = token.trim();
  if (!trimmed || INVALID_TOKENS.has(trimmed)) {
    return "invalid";
  }
  return "present";
}

export function pushTokenTail(token) {
  if (!token || typeof token !== "string") {
    return null;
  }
  return token.length > 8 ? token.slice(-8) : token;
}

export async function appendDispatchLog(bookingId, entry) {
  if (!bookingId || !entry) {
    return;
  }
  const { dispatchLogRef } = firebase;
  const logRef = push(dispatchLogRef(bookingId));
  await set(logRef, {
    at: Date.now(),
    ...entry,
  });
}

export async function recordBookingDispatchMeta(bookingId, data, settings, bookingDataInput) {
  if (!bookingId) {
    return;
  }
  const { dispatchMetaRef } = firebase;
  const requested = data.requestedDrivers || {};
  const preRequested = data.preRequestedDrivers || {};
  const estimates = data.driverEstimates || {};
  const requestedKeys = Object.keys(requested);
  const preRequestedKeys = Object.keys(preRequested);

  await set(dispatchMetaRef(bookingId), {
    recordedAt: Date.now(),
    bookingStatus: data.status || null,
    bookLater: !!data.bookLater,
    carType: data.carType || null,
    payment_mode: data.payment_mode || null,
    prepaid: !!data.prepaid,
    autoDispatchEnabled: !!(settings && settings.autoDispatch),
    customer: data.customer || null,
    pickup: data.pickup
      ? { lat: data.pickup.lat, lng: data.pickup.lng }
      : null,
    requestedDriverCount: requestedKeys.length,
    preRequestedDriverCount: preRequestedKeys.length,
    driverEstimateCount: Object.keys(estimates).length,
    hadRequestedDrivers: requestedKeys.length > 0,
    hadPreRequestedDrivers: preRequestedKeys.length > 0,
  });

  const allUids = new Set([...requestedKeys, ...preRequestedKeys, ...Object.keys(estimates)]);

  for (const driverUid of allUids) {
    const estimate = estimates[driverUid] || {};
    await appendDispatchLog(bookingId, {
      event: "candidate_recorded",
      source: "addBooking",
      driverUid,
      listType: requested[driverUid]
        ? "requestedDrivers"
        : preRequested[driverUid]
          ? "preRequestedDrivers"
          : "driverEstimates_only",
      distance: estimate.distance ?? null,
      timein_text: estimate.timein_text ?? null,
      bookingStatus: data.status || null,
    });
  }

  if (allUids.size === 0) {
    await appendDispatchLog(bookingId, {
      event: "no_candidates",
      source: "addBooking",
      bookingStatus: data.status || null,
      note:
        "No requestedDrivers, preRequestedDrivers, or driverEstimates on create",
    });
  }
}

export async function logPushSkipped(bookingId, driverUid, profile, reason) {
  await appendDispatchLog(bookingId, {
    event: "push_skipped",
    source: "addBooking",
    driverUid,
    reason,
    platform: profile?.userPlatform || null,
    pushTokenStatus: getPushTokenStatus(profile?.pushToken),
    pushTokenTail: pushTokenTail(profile?.pushToken),
  });
}

export async function notifyDriversForNewBooking(bookingId, bookingData, language) {
  const { singleUserRef } = firebase;
  const drivers = {
    ...(bookingData.requestedDrivers || {}),
    ...(bookingData.preRequestedDrivers || {}),
  };
  const uids = Object.keys(drivers);
  if (!uids.length) {
    return;
  }

  await Promise.all(
    uids.map(
      (uid) =>
        new Promise((resolve) => {
          onValue(
            singleUserRef(uid),
            async (snapshot) => {
              const profile = snapshot.val();
              const pushToken = profile?.pushToken;
              const pushTokenStatus = getPushTokenStatus(pushToken);

              if (pushTokenStatus !== "present") {
                await logPushSkipped(
                  bookingId,
                  uid,
                  profile,
                  pushTokenStatus === "missing"
                    ? "no_push_token"
                    : "invalid_push_token",
                );
                resolve();
                return;
              }

              RequestPushMsg(pushToken, {
                title: language.notification_title,
                msg: language.new_booking_notification,
                screen: "DriverTrips",
                notificationType: "trip_request",
                channelId: "trip_requests_v2",
                sound: "horn.wav",
                bookingId,
                driverUid: uid,
              });

              await appendDispatchLog(bookingId, {
                event: "push_requested",
                source: "addBooking",
                driverUid: uid,
                platform: profile?.userPlatform || null,
                pushTokenStatus: "present",
                pushTokenTail: pushTokenTail(pushToken),
              });
              resolve();
            },
            { onlyOnce: true },
          );
        }),
    ),
  );
}
